import { useState, useCallback } from 'react';
import { useInvoices, useSaveInvoice, useDeleteInvoice, useCreateCreditNote, INVOICE_STATUSES } from '../hooks/useInvoices';
import { useSavePayment, type PaymentMethod } from '@/features/payments/hooks/usePayments';
import { generateInvoicePdf, InvoicePdfData } from '../services/generateInvoicePdf';
import { generatePdpZip } from '../services/exportPdpZip';
import { useProfile, useProfileBilling } from '@/shared/hooks/useCloudData';
import { FileText, Trash2, Download, Plus, Eye, Package, FileX, Banknote } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import type { Invoice } from '../hooks/useInvoices';

// ── Builder: Invoice + profile → InvoicePdfData ──────────────────────────────

function buildPdfData(inv: Invoice, profile: any, billing: any): InvoicePdfData {
  const sellerAddr = (profile?.business_address as Record<string, string>) || {};
  const buyerAddr  = (inv.client_address as Record<string, string>) || {};
  return {
    invoiceNumber:  inv.invoice_number,
    issueDate:      inv.issue_date  || new Date().toISOString().split('T')[0],
    dueDate:        inv.due_date    || undefined,
    sellerName:     profile?.business_name || profile?.display_name || '',
    sellerEmail:    profile?.email  || '',
    sellerSiret:    billing?.siret  || (profile as any)?.siret     || '',
    sellerVatNumber: billing?.vat_number || (profile as any)?.vat_number || '',
    sellerAddress: {
      street:     sellerAddr.street,
      city:       sellerAddr.city,
      postalCode: sellerAddr.postal_code,
    },
    buyerName:     inv.client_name  || '',
    buyerEmail:    inv.client_email || '',
    buyerAddress: {
      street:     buyerAddr.street,
      city:       buyerAddr.city,
      postalCode: buyerAddr.postal_code,
    },
    items:       inv.items       || [],
    totalHt:     inv.total_ht   ?? 0,
    totalVat:    inv.total_vat  ?? 0,
    totalTtc:    inv.total_ttc  ?? 0,
    paymentMethod: (inv.payment_method && inv.payment_method !== 'cash') ? inv.payment_method : undefined,
    notes:         inv.notes         || undefined,
    headerColor:   billing?.invoice_color  || undefined,
    invoiceFooter: billing?.invoice_footer || undefined,
  };
}

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft:       'outline',
  sent:        'secondary',
  paid:        'default',
  cancelled:   'destructive',
  credit_note: 'secondary',
};

export default function Invoices() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { data: invoices = [], isLoading } = useInvoices();
  const saveInvoice      = useSaveInvoice();
  const deleteInvoice    = useDeleteInvoice();
  const createCreditNote = useCreateCreditNote();
  const { data: profile } = useProfile();
  const { data: billing } = useProfileBilling();

  const savePayment = useSavePayment();

  const [generating,   setGenerating]  = useState<string | null>(null);
  const [previewUrl,   setPreviewUrl]  = useState<string | null>(null);
  const [previewInv,   setPreviewInv]  = useState<Invoice | null>(null);
  const [searchQuery,  setSearchQuery] = useState('');

  // ── Pay dialog state ────────────────────────────────────────────────────────
  const [payInv,       setPayInv]      = useState<Invoice | null>(null);
  const [payAmount,    setPayAmount]   = useState('');
  const [payMethod,    setPayMethod]   = useState<PaymentMethod>('cash');
  const [payDate,      setPayDate]     = useState(new Date().toISOString().split('T')[0]);

  const openPayDialog = (inv: Invoice) => {
    setPayInv(inv);
    setPayAmount(((inv.total_ttc ?? inv.total_ht ?? 0)).toFixed(2));
    setPayMethod('cash');
    setPayDate(new Date().toISOString().split('T')[0]);
  };

  const confirmPay = () => {
    if (!payInv) return;
    const amount = parseFloat(payAmount);
    if (isNaN(amount) || amount <= 0) { toast.error('Montant invalide'); return; }
    savePayment.mutate(
      {
        client_name:  payInv.client_name || '',
        description:  `Règlement facture ${payInv.invoice_number}`,
        amount,
        method:       payMethod,
        status:       'paid',
        payment_date: payDate,
        client_id:    payInv.client_id || undefined,
      },
      {
        onSuccess: () => {
          saveInvoice.mutate(
            { ...payInv, items: payInv.items ?? [], status: 'paid', payment_method: payMethod },
            { onSuccess: () => { toast.success('Facture marquée payée'); setPayInv(null); } },
          );
        },
        onError: () => toast.error('Erreur lors de l\'enregistrement du paiement'),
      },
    );
  };

  const filtered = searchQuery
    ? invoices.filter(inv => inv.invoice_number === searchQuery)
    : invoices;

  const statusLabel = (status: string) =>
    INVOICE_STATUSES.find((s) => s.value === status)?.label[language as 'fr' | 'en' | 'es'] ?? status;

  const handlePreview = useCallback(async (inv: Invoice) => {
    setGenerating(inv.id);
    try {
      const { pdfBlob } = await generateInvoicePdf(buildPdfData(inv, profile, billing));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(pdfBlob));
      setPreviewInv(inv);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(null);
    }
  }, [profile, billing, previewUrl]);

  const handleDownloadPdf = useCallback(async (inv: Invoice) => {
    setGenerating(inv.id + '-pdf');
    try {
      const { pdfBlob } = await generateInvoicePdf(buildPdfData(inv, profile, billing));
      const url = URL.createObjectURL(pdfBlob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `${inv.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(null);
    }
  }, [profile, billing]);

  const handleDownloadZip = useCallback(async (inv: Invoice) => {
    setGenerating(inv.id + '-zip');
    try {
      const zipBlob = await generatePdpZip(buildPdfData(inv, profile, billing));
      const url = URL.createObjectURL(zipBlob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `${inv.invoice_number}_PDP.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de la génération du ZIP');
    } finally {
      setGenerating(null);
    }
  }, [profile, billing]);

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewInv(null);
  };

  const handleDelete = (id: string) => {
    deleteInvoice.mutate(id, { onSuccess: () => toast.success('Facture supprimée') });
  };

  const handleAvoir = (inv: Invoice) => {
    createCreditNote.mutate(inv.id, {
      onSuccess: () => toast.success(`Avoir créé pour ${inv.invoice_number}`),
      onError: (err: any) => toast.error(err?.message ?? 'Erreur lors de la création de l\'avoir'),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="text-primary" size={24} />
            <h1 className="text-2xl font-bold font-display">Factures</h1>
          </div>
          <div className="flex items-center gap-2">
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
              >
                {searchQuery} ×
              </button>
            )}
            <Button onClick={() => navigate('/invoices/new')} className="gap-2">
              <Plus size={16} /> Nouvelle facture
            </Button>
          </div>
        </div>

        {invoices.length === 0 && (
          <div className="glass-card p-12 text-center">
            <FileText className="mx-auto text-muted-foreground mb-3" size={32} />
            <p className="text-muted-foreground">Aucune facture pour le moment.</p>
          </div>
        )}

        <ul className="space-y-3">
          {filtered.map((inv) => {
            const isGenerating = generating === inv.id || generating === inv.id + '-pdf' || generating === inv.id + '-zip';
            return (
              <li key={inv.id} className="glass-card p-4">
                <div className="flex items-center gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{inv.invoice_number}</p>
                      <Badge variant={STATUS_VARIANTS[inv.status] ?? 'outline'}>
                        {statusLabel(inv.status)}
                      </Badge>
                      {inv.status === 'credit_note' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20 font-medium">
                          Avoir
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate mt-0.5">
                      {inv.client_name || 'Client inconnu'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {inv.issue_date
                        ? new Date(inv.issue_date).toLocaleDateString('fr-FR')
                        : new Date(inv.created_at).toLocaleDateString('fr-FR')}
                    </p>
                    {inv.credit_note_for && (() => {
                      const num = invoices.find(i => i.id === inv.credit_note_for)?.invoice_number;
                      return num ? (
                        <button onClick={() => setSearchQuery(num)}
                          className="text-xs text-amber-600 hover:underline underline-offset-2 mt-0.5 text-left">
                          Avoir de : {num}
                        </button>
                      ) : null;
                    })()}
                    {inv.cancelled_by && (() => {
                      const num = invoices.find(i => i.id === inv.cancelled_by)?.invoice_number;
                      return num ? (
                        <button onClick={() => setSearchQuery(num)}
                          className="text-xs text-muted-foreground hover:text-primary hover:underline underline-offset-2 mt-0.5 text-left">
                          Annulée → avoir {num}
                        </button>
                      ) : null;
                    })()}
                  </div>

                  {/* Montants */}
                  {(() => {
                    const itemsHt = (inv.items ?? []).reduce((s, it) => s + (it.quantity ?? 1) * (it.unitPrice ?? 0), 0);
                    const ht  = inv.total_ht  ?? (itemsHt || 0);
                    const ttc = inv.total_ttc ?? inv.total_ht ?? (itemsHt || 0);
                    return (
                      <div className="text-right shrink-0">
                        <p className="font-bold text-base tabular-nums">{ttc.toFixed(2)} €</p>
                        <p className="text-xs text-muted-foreground">HT {ht.toFixed(2)} €</p>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Marquer payée */}
                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                      <Button size="sm" variant="outline" className="gap-1.5"
                        onClick={() => openPayDialog(inv)}>
                        <Banknote size={13} /> Marquer payée
                      </Button>
                    )}

                    {/* Aperçu PDF */}
                    <Button size="icon" variant="ghost" className="h-8 w-8"
                      title="Aperçu PDF"
                      onClick={() => handlePreview(inv)}
                      disabled={isGenerating}>
                      <Eye size={14} />
                    </Button>

                    {/* Télécharger PDF */}
                    <Button size="icon" variant="ghost" className="h-8 w-8"
                      title="Télécharger PDF (Factur-X)"
                      onClick={() => handleDownloadPdf(inv)}
                      disabled={isGenerating}>
                      <Download size={14} />
                    </Button>

                    {/* Export PDP/ZIP */}
                    <Button size="icon" variant="ghost" className="h-8 w-8"
                      title="Exporter PDP/PPF (ZIP)"
                      onClick={() => handleDownloadZip(inv)}
                      disabled={isGenerating}>
                      <Package size={14} />
                    </Button>

                    {/* Supprimer — brouillon uniquement */}
                    {inv.status === 'draft' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Supprimer"
                            disabled={deleteInvoice.isPending}>
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer la facture ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {inv.invoice_number} sera définitivement supprimée.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(inv.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}

                    {/* Émettre un avoir — sent ou paid uniquement */}
                    {(inv.status === 'sent' || inv.status === 'paid') && (
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                        title="Émettre un avoir"
                        onClick={() => handleAvoir(inv)}
                        disabled={createCreditNote.isPending}>
                        <FileX size={14} className="text-amber-500" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Spinner génération en cours */}
                {isGenerating && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    Génération en cours…
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Aperçu PDF ── */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>{previewInv?.invoice_number} — Aperçu</DialogTitle>
            <DialogDescription className="sr-only">Aperçu du PDF de la facture</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {previewUrl && (
              <object
                data={previewUrl}
                type="application/pdf"
                className="w-full h-[70vh]"
              >
                <iframe src={previewUrl} className="w-full h-[70vh]" title="Aperçu facture" />
              </object>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={closePreview}>Fermer</Button>
            {previewInv && (
              <Button onClick={() => handleDownloadPdf(previewInv)} className="gap-2">
                <Download size={14} /> Télécharger
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog marquer payée ── */}
      <Dialog open={!!payInv} onOpenChange={(open) => { if (!open) setPayInv(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Marquer comme payée</DialogTitle>
            <DialogDescription>{payInv?.invoice_number} — {payInv?.client_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Montant reçu (€)</label>
              <input
                type="number" min="0" step="0.01"
                value={payAmount}
                onChange={e => setPayAmount(e.target.value)}
                className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Mode de paiement</label>
              <select
                value={payMethod}
                onChange={e => setPayMethod(e.target.value as PaymentMethod)}
                className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
              >
                <option value="cash">Espèces</option>
                <option value="card">Carte bancaire</option>
                <option value="transfer">Virement</option>
                <option value="check">Chèque</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Date de paiement</label>
              <input
                type="date"
                value={payDate}
                onChange={e => setPayDate(e.target.value)}
                className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayInv(null)}>Annuler</Button>
            <Button onClick={confirmPay} disabled={savePayment.isPending || saveInvoice.isPending}>
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
